/**
 * PreviewConsentBanner — first-load / re-prompt consent UI for an HTML
 * preview's root scope.
 *
 * Renders when `usePreviewSession` reports a `pendingConsent`. The user
 * reads the proposed path and chooses one of:
 *   - [Allow Read Access]: consent is stored; iframe loads with the
 *     proposed root, all referenced files resolve.
 *   - [Single File Mode]: iframe renders the HTML alone via srcDoc;
 *     no folder access is granted, so cross-file refs (CSS, images,
 *     JS) don't resolve. Useful for a quick peek at an untrusted
 *     file without granting anything.
 *
 * Users who want to dismiss entirely can close the tab via the tab's
 * X button. Users wanting a narrower-but-functional scope can Allow,
 * then use the toolbar [Change…] picker to set a tighter explicit root.
 *
 * See docs/plans/2026-05-21_PREVIEW_SANDBOX.md → "Consent state machine".
 */
import { ShieldCheck, FolderSearch, FolderTree, Folder, File, Sparkles } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { filesApi } from '@papan/files/client'
import { request, errorText } from '@papan/api-client'
import { useToast, isMouseHover } from '@papan/core-ui/ui'
import { encodeServePath } from '../client/serveToken.js'

interface PreviewConsentBannerProps {
  /** VFS path of the proposed root, e.g. "/papan/dashboards". */
  proposedRootPath: string
  /** File ID of the proposed root (source-tenant native id) — used for the
   *  peek-iframe mint + consent keying. */
  proposedRootId: string
  /** Recipient-addressable id for LISTING the proposed root's children in the
   *  folder-tree popover. Set for `.shared` previews, where `proposedRootId` is
   *  a source-tenant id that lists empty in the recipient's tenant; the compound
   *  mount id resolves cross-tenant. Falls back to `proposedRootId` (same-tenant). */
  browseRootId?: string
  /** The HTML file id — used to spin up the no-script peek iframe. */
  fileId: string
  /** Preview origin (subdomain) for the no-script peek iframe. */
  previewOrigin: string
  /** Path of the entry file RELATIVE TO the proposed root (e.g.
   *  "pages/nested.html"). Undefined while still resolving — peek
   *  iframe won't render until this is known. */
  entryFilePath: string | undefined
  /** Approve and create the session. */
  onAllow: () => Promise<void> | void
  /** Render the HTML in single-file mode — srcDoc only, no folder
   *  access. The iframe shows the file but cross-file refs don't load.
   *  Drive-view only; omit in the share-view (SFM has no share content). */
  onSingleFile?: () => void
  /** (share-view) Back out of the pending root change without switching. Rendered
   *  as a "Cancel" button when `onSingleFile` isn't provided. */
  onCancel?: () => void
  /** (share-view) Skip the no-script peek iframe — it mints a Drive preview session,
   *  which a cross-tenant recipient can't. */
  disablePeek?: boolean
  /** (share-view) Skip the hover folder-tree popover — it lists children via Drive
   *  auth, which a cross-tenant recipient can't. */
  disableTree?: boolean
  /** Optional: hand off to the AI assistant to review the HTML for the user.
   *  When provided, renders an "AI risk check" middle button. */
  onAskAiReview?: () => void
  /** Optional: click on the folder badge to reveal/highlight the folder
   *  in the workspace file explorer. */
  onRevealRoot?: () => void
}

/** Show just the folder NAME from a path like "/papan/foo/dashboards". */
function folderNameFromPath(path: string): string {
  const segments = path.split('/').filter(Boolean)
  return segments[segments.length - 1] || '/'
}

interface TreeNode {
  id: string
  name: string
  isFolder: boolean
  /** Loaded for level-1 folders; undefined means not yet fetched. */
  children?: TreeNode[]
  /** True when there are more children than we're showing. */
  truncated?: boolean
}

const L1_LIMIT = 3
const L2_LIMIT = 1

/**
 * Popover showing the folder's contents — up to 3 children at level 1
 * and 1 child inside each level-1 folder. Fetched lazily on first
 * hover so the banner stays cheap when the user just glances at it.
 */
function FolderTreePreview({ rootId }: { rootId: string }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [l1, setL1] = useState<TreeNode[]>([])
  const [l1Truncated, setL1Truncated] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        // Fetch root's direct children, ask for one more than L1_LIMIT so
        // we can mark the list as truncated when there are extras.
        const r = await filesApi.listChildren(rootId, { limit: L1_LIMIT + 1 })
        if (cancelled) return
        const trimmed = r.files.slice(0, L1_LIMIT)
        const truncated = r.total > L1_LIMIT
        const nodes: TreeNode[] = trimmed.map((f) => ({
          id: f.id,
          name: f.name,
          isFolder: f.isFolder,
        }))
        // Fan out: fetch L2 for each level-1 folder in parallel.
        const folderNodes = nodes.filter((n) => n.isFolder)
        const l2Results = await Promise.all(
          folderNodes.map(async (n) => {
            try {
              const sub = await filesApi.listChildren(n.id, { limit: L2_LIMIT + 1 })
              return {
                id: n.id,
                children: sub.files.slice(0, L2_LIMIT).map((f) => ({
                  id: f.id,
                  name: f.name,
                  isFolder: f.isFolder,
                })),
                truncated: sub.total > L2_LIMIT,
              }
            } catch {
              return { id: n.id, children: [], truncated: false }
            }
          }),
        )
        if (cancelled) return
        const l2Map = new Map(l2Results.map((r) => [r.id, r]))
        for (const n of nodes) {
          const sub = l2Map.get(n.id)
          if (sub) {
            n.children = sub.children
            n.truncated = sub.truncated
          }
        }
        setL1(nodes)
        setL1Truncated(truncated)
      } catch (e) {
        const text = errorText(e, 'Failed to load')   // #1316: a dropped answer shows nothing
        if (!cancelled && text !== null) setError(text)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [rootId])

  return (
    <div className="text-xs text-left font-mono themed-text">
      {loading && (
        <div className="themed-text-muted text-[11px] pl-6">Loading…</div>
      )}
      {error && (
        <div className="text-red-400 text-[11px] pl-6">{error}</div>
      )}
      {!loading && !error && l1.length === 0 && (
        <div className="themed-text-muted text-[11px] pl-5">(empty)</div>
      )}
      {!loading && !error && l1.map((n) => (
        <div key={n.id}>
          <div className="flex items-center gap-1.5 pl-5">
            {n.isFolder ? (
              <Folder size={11} className="themed-text-muted shrink-0" />
            ) : (
              <File size={11} className="themed-text-muted shrink-0" />
            )}
            <span className="themed-text-secondary truncate">{n.name}</span>
          </div>
          {n.children?.map((c) => (
            <div key={c.id} className="flex items-center gap-1.5 pl-12">
              {c.isFolder ? (
                <Folder size={11} className="themed-text-muted shrink-0" />
              ) : (
                <File size={11} className="themed-text-muted shrink-0" />
              )}
              <span className="themed-text-muted truncate">{c.name}</span>
            </div>
          ))}
          {n.truncated && (
            <div className="pl-12 themed-text-muted text-[10px]">…more</div>
          )}
        </div>
      ))}
      {l1Truncated && (
        <div className="pl-6 themed-text-muted text-[10px]">…more</div>
      )}
    </div>
  )
}

export function PreviewConsentBanner({
  proposedRootPath,
  proposedRootId,
  browseRootId,
  fileId,
  previewOrigin,
  entryFilePath,
  onAllow,
  onSingleFile,
  onCancel,
  disablePeek,
  disableTree,
  onAskAiReview,
  onRevealRoot,
}: PreviewConsentBannerProps) {
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  // Hover-driven popover. Mounted-on-hover (not just opacity-toggled) so
  // we don't fetch the tree until the user actually hovers — keeps the
  // banner cheap when the user just glances at it.
  const [showTreePopover, setShowTreePopover] = useState(false)
  // #1426: in the span form (no `onRevealRoot` — the app run views, on a phone) the tree is the only touch path to
  // what the app would read, so an inert tap opens it; it stays until the next press outside the badge + popover.
  // The button form keeps it mouse-only: there a tap reveals the root.
  const [treeOpenedByTap, setTreeOpenedByTap] = useState(false)
  const badgeAreaRef = useRef<HTMLDivElement>(null)
  const lastPointerTypeRef = useRef('')
  useEffect(() => {
    if (!treeOpenedByTap) return
    const onPress = (e: PointerEvent) => {
      if (badgeAreaRef.current?.contains(e.target as Node)) return
      setTreeOpenedByTap(false)
      setShowTreePopover(false)
    }
    document.addEventListener('pointerdown', onPress, true)
    return () => document.removeEventListener('pointerdown', onPress, true)
  }, [treeOpenedByTap])

  // Peek session — a no-script preview the user can glance at BEFORE
  // clicking Allow. Server enforces `script-src 'none'` so the HTML
  // renders styled but inert. We mount it behind the consent panel with
  // a blur + theme-color overlay so the user sees a fuzzy hint of what
  // they're allowing without being able to read sensitive contents.
  const [peekSessionId, setPeekSessionId] = useState<string | null>(null)
  // Ref mirror for explicit cleanup-on-Allow (handleAllow can't read
  // state captured in the effect's closure; the ref is always fresh).
  const peekSessionIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (disablePeek) return  // (share-view) no Drive preview peek
    let cancelled = false
    ;(async () => {
      try {
        const r = await request<{ sessionId: string }>('/api/preview/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileId,
            rootId: proposedRootId,
            mode: 'view',
            noScripts: true,
          }),
        })
        if (cancelled) {
          // Clean up if unmount raced ahead — orphaned sessions cost RAM.
          request(`/api/preview/session/${r.sessionId}`, { method: 'DELETE' }).catch(() => {})
          return
        }
        peekSessionIdRef.current = r.sessionId
        setPeekSessionId(r.sessionId)
      } catch {
        // Peek is a bonus visual; failure just leaves the overlay blank.
      }
    })()
    return () => {
      cancelled = true
      const id = peekSessionIdRef.current
      if (id) {
        peekSessionIdRef.current = null
        request(`/api/preview/session/${id}`, { method: 'DELETE' }).catch(() => {})
      }
    }
  }, [fileId, proposedRootId, disablePeek])

  const peekUrl = peekSessionId && previewOrigin && entryFilePath
    ? `${previewOrigin}/s/${peekSessionId}/${encodeServePath(entryFilePath)}`
    : null

  const handleAllow = async () => {
    if (busy) return
    setBusy(true)
    try {
      await onAllow()
      // Drop the peek session on success — the real session is up
      // now, so the peek's job is done and there's no reason to sit on
      // its RAM until the 24h TTL. Done after onAllow rather than
      // before, so a failed Allow (500, network blip) keeps the peek
      // alive — otherwise the user would see the consent banner still
      // up but with a blank blurred preview behind it. Clearing the
      // ref first so the unmount handler can't double-delete.
      const peekId = peekSessionIdRef.current
      if (peekId) {
        peekSessionIdRef.current = null
        request(`/api/preview/session/${peekId}`, { method: 'DELETE' }).catch(() => {})
      }
    } catch (err: any) {
      // Surface to the user — otherwise the banner just stays up with
      // no signal that Allow failed (caller's promise rejection is
      // otherwise silent). Peek stays alive on this path so the user
      // can read the blurred preview while deciding whether to retry.
      toast.error(err?.message || 'Failed to grant access')
    } finally {
      setBusy(false)
    }
  }

  // Inline panel — centered in the editor preview area. Visual style:
  // shield icon (security-coded, not alarm-coded), readable body text
  // (themed-text-secondary, not opacity-reduced muted), and the proposed
  // folder path highlighted with an icon + monofont + bg + border so it
  // stands out as the load-bearing thing the user must read before Allow.
  return (
    // A stable hook for the scope/consent flows. Their assertions used to grep
    // the WHOLE PAGE for a folder name, which was unambiguous only because this
    // banner was the sole surface showing one — no longer true now that a drive
    // double-click opens the single-file window, whose title bar carries the
    // file's full path.
    <div data-testid="preview-consent-banner" className="w-full h-full flex items-center justify-center themed-bg-primary relative overflow-hidden">
      {/* No-script peek behind the consent panel: a blurred render of
          the HTML with strict CSP (script-src 'none'). Gives the user a
          visual hint of the page they're about to allow, without
          granting any JS-capable access. The theme-colored overlay
          dims it heavily so the peek is a vibe, not a readable preview. */}
      {peekUrl && (
        <>
          <iframe
            src={peekUrl}
            title="Preview (inert)"
            aria-hidden="true"
            tabIndex={-1}
            sandbox=""
            className="absolute inset-0 w-full h-full border-0 pointer-events-none"
            style={{ filter: 'blur(14px)' }}
          />
          {/* Theme-bg overlay — pulls the same CSS variable the surrounding
              themed-bg-primary uses, so the tint matches the active theme
              (dark/dim/light). Heavy alpha keeps the blur as a vibe-only
              indicator that won't accidentally leak readable contents. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{ backgroundColor: 'var(--bg-primary)', opacity: 0.7 }}
          />
        </>
      )}
      <div className="text-center max-w-md px-6 pb-16 relative z-10">
        <FolderSearch size={48} className="mx-auto mb-4 text-yellow-400" />
        <p className="themed-text text-lg mb-3">Read access, ok?</p>
        {/* Folder badge + inline tree expansion. On hover, the tree
            renders as a continuation of the badge — same bg/border, no
            separate panel — but positioned absolute so it doesn't push
            the buttons below down when it opens. The badge's bottom
            corners square off so they meet the expansion seamlessly. */}
        <div
          ref={badgeAreaRef}
          className="relative inline-block mb-4"
          // #1426: only a mouse hovers — a touch's pointerleave (every tap ends with one) never closes a tapped-open tree
          onPointerEnter={(e) => { if (!disableTree && isMouseHover(e)) setShowTreePopover(true) }}
          onPointerLeave={(e) => { if (isMouseHover(e)) { setShowTreePopover(false); setTreeOpenedByTap(false) } }}
          onPointerDown={(e) => { lastPointerTypeRef.current = e.pointerType }}
          // the span form: a non-mouse tap (no action there) opens the tree; the button form's tap reveals the root
          onClick={() => {
            if (onRevealRoot || disableTree || lastPointerTypeRef.current === 'mouse') return
            setShowTreePopover(true)
            setTreeOpenedByTap(true)
          }}
        >
          {onRevealRoot ? (
            <button
              type="button"
              onClick={onRevealRoot}
              className={`inline-flex items-center justify-center gap-2 px-3 py-2 themed-bg-tertiary border themed-border cursor-pointer hover:themed-bg-secondary transition-colors ${
                showTreePopover ? 'rounded-t' : 'rounded'
              }`}
            >
              <FolderTree size={14} className="text-yellow-400 shrink-0" />
              <span className="text-sm font-mono themed-text break-all text-center">
                {folderNameFromPath(proposedRootPath)}
              </span>
            </button>
          ) : (
            <span
              className={`inline-flex items-center justify-center gap-2 px-3 py-2 themed-bg-tertiary border themed-border ${
                showTreePopover ? 'rounded-t' : 'rounded'
              }`}
            >
              <FolderTree size={14} className="text-yellow-400 shrink-0" />
              <span className="text-sm font-mono themed-text break-all text-center">
                {folderNameFromPath(proposedRootPath)}
              </span>
            </span>
          )}
          {showTreePopover && !disableTree && (
            <div className="absolute left-0 right-0 top-full z-50 themed-bg-tertiary border-x border-b themed-border rounded-b px-2 py-1.5 text-left min-w-[220px]">
              <FolderTreePreview rootId={browseRootId ?? proposedRootId} />
            </div>
          )}
        </div>
        <p className="text-sm themed-text-secondary mb-1">
          Allowing lets the HTML — and any JavaScript it runs — read every
          file inside that folder.{' '}
          <span className="font-semibold">Do you trust the author?</span>
        </p>
        {onAskAiReview && (
          <button
            onClick={onAskAiReview}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-sm text-purple-300 hover:text-purple-200 mb-5 cursor-pointer disabled:opacity-50"
          >
            <Sparkles size={14} className="text-purple-400" />
            AI risk check
          </button>
        )}
        <div className="flex flex-col items-stretch gap-2 max-w-[220px] mx-auto">
          <button
            onClick={handleAllow}
            disabled={busy}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm themed-btn-primary rounded transition-colors disabled:opacity-50"
          >
            <ShieldCheck size={14} className="shrink-0" />
            {busy ? 'Allowing…' : 'Allow'}
          </button>
          {onSingleFile ? (
            <button
              onClick={onSingleFile}
              disabled={busy}
              className="px-4 py-2 text-sm themed-text-secondary hover:themed-text rounded transition-colors disabled:opacity-50"
            >
              Single File Mode
            </button>
          ) : onCancel ? (
            <button
              onClick={onCancel}
              disabled={busy}
              className="px-4 py-2 text-sm themed-text-secondary hover:themed-text rounded transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
